/**
 * Framed product showcase: cycle product images on media hover with crossfade.
 * Bottom layer stays opaque during fade-in to avoid white bleed-through.
 */
(function () {
  const XLINK = "http://www.w3.org/1999/xlink";
  const FADE_MS = 650;
  const HOVER_DELAY_MS = 200;
  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function readImageUrls(container) {
    const jsonEl = container.querySelector("script[data-fps-image-urls]");
    const raw = jsonEl ? jsonEl.textContent : container.getAttribute("data-image-urls");

    if (!raw) return [];

    try {
      const urls = JSON.parse(raw.trim());
      return Array.isArray(urls) ? urls.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function resolveUrl(url, index) {
    if (typeof index !== "number") return url;
    const cacheKey = (url.includes("?") ? "&" : "?") + "fps_i=" + index;
    return url + cacheKey;
  }

  function setImageHref(image, url) {
    if (!image || !url) return;

    image.setAttribute("href", url);
    image.setAttributeNS(XLINK, "href", url);

    if (image.href && typeof image.href.baseVal === "string") {
      image.href.baseVal = url;
    }
  }

  function preloadUrl(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function waitForOpacityTransition(element) {
    return new Promise((resolve) => {
      let done = false;

      const finish = () => {
        if (done) return;
        done = true;
        element.removeEventListener("transitionend", onEnd);
        resolve();
      };

      const onEnd = (event) => {
        if (event.target === element && event.propertyName === "opacity") {
          finish();
        }
      };

      element.addEventListener("transitionend", onEnd);
      window.setTimeout(finish, FADE_MS + 80);
    });
  }

  function keepImagesBelowBorders(frame) {
    const svg = frame.querySelector(".frame-svg");
    if (!svg) return;

    const border = svg.querySelector(".outer-border");
    if (!border) return;

    svg.querySelectorAll(".frame-image").forEach((image) => {
      svg.insertBefore(image, border);
    });
  }

  function ensureOverlay(frame) {
    let base = frame.querySelector(".frame-image:not(.frame-image--overlay)");
    let overlay = frame.querySelector(".frame-image--overlay");

    if (!base) return null;

    if (!overlay) {
      overlay = base.cloneNode(true);
      overlay.classList.add("frame-image--overlay");
      overlay.setAttribute("aria-hidden", "true");
      const current = base.getAttribute("href") || base.getAttributeNS(XLINK, "href");
      if (current) setImageHref(overlay, current);

      const svg = base.parentNode;
      const border = svg && svg.querySelector(".outer-border");
      if (border) {
        svg.insertBefore(overlay, border);
      } else {
        base.after(overlay);
      }
    }

    frame.setAttribute("data-fps-crossfade", "");
    keepImagesBelowBorders(frame);
    return { base, overlay };
  }

  function createCrossfader(frame) {
    const layers = ensureOverlay(frame);
    if (!layers) return null;

    let active = layers.base;
    let inactive = layers.overlay;

    function syncCssVar(url) {
      frame.style.setProperty("--image", 'url("' + url.replace(/"/g, "%22") + '")');
    }

    function normalizeLayers() {
      active.style.opacity = "1";
      inactive.style.opacity = "0";
    }

    function bringToFront(layer) {
      const svg = layer.parentNode;
      if (!svg) return;

      const border = svg.querySelector(".outer-border");
      if (border && border.previousElementSibling !== layer) {
        svg.insertBefore(layer, border);
      }
    }

    async function crossfadeTo(url, options) {
      const immediate = options && options.immediate;

      await preloadUrl(url);

      if (immediate || REDUCED_MOTION) {
        setImageHref(active, url);
        setImageHref(inactive, url);
        bringToFront(active);
        normalizeLayers();
        syncCssVar(url);
        return;
      }

      setImageHref(inactive, url);
      bringToFront(inactive);
      inactive.style.opacity = "0";
      active.style.opacity = "1";

      await wait(20);

      inactive.style.opacity = "1";
      await waitForOpacityTransition(inactive);

      active.style.opacity = "0";

      const previousActive = active;
      active = inactive;
      inactive = previousActive;

      normalizeLayers();
      keepImagesBelowBorders(frame);
      syncCssVar(url);
    }

    let busy = false;
    let queued = null;

    async function show(url, options) {
      if (busy) {
        queued = { url, options };
        return;
      }

      busy = true;

      try {
        await crossfadeTo(url, options);
      } finally {
        busy = false;

        if (queued) {
          const next = queued;
          queued = null;
          show(next.url, next.options);
        }
      }
    }

    normalizeLayers();
    return { show };
  }

  function bindMedia(container) {
    const urls = readImageUrls(container);
    if (urls.length <= 1) return;

    const frame = container.querySelector("[data-special-frame]");
    if (!frame) return;

    if (container.dataset.fpsProductMediaBound === "true") return;
    container.dataset.fpsProductMediaBound = "true";

    const crossfader = createCrossfader(frame);
    if (!crossfader) return;

    const interval = Math.max(800, parseInt(container.getAttribute("data-interval"), 10) || 2000);
    const hoverTarget = container;
    let timer = null;
    let hoverDelayTimer = null;
    let index = 0;

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function cancelHoverDelay() {
      if (hoverDelayTimer) {
        clearTimeout(hoverDelayTimer);
        hoverDelayTimer = null;
      }
    }

    function showAt(i, options) {
      crossfader.show(resolveUrl(urls[i], i), options);
    }

    function reset() {
      cancelHoverDelay();
      stop();
      index = 0;
      showAt(0, { immediate: true });
    }

    function startCarousel() {
      if (urls.length <= 1) return;

      stop();
      index = 1;
      showAt(index);

      timer = setInterval(() => {
        index += 1;
        if (index >= urls.length) index = 1;
        showAt(index);
      }, interval);
    }

    function scheduleCarouselStart() {
      if (REDUCED_MOTION) {
        startCarousel();
        return;
      }

      cancelHoverDelay();
      hoverDelayTimer = window.setTimeout(() => {
        hoverDelayTimer = null;
        startCarousel();
      }, HOVER_DELAY_MS);
    }

    hoverTarget.addEventListener("pointerenter", scheduleCarouselStart);
    hoverTarget.addEventListener("pointerleave", reset);
    hoverTarget.addEventListener("focusin", scheduleCarouselStart);
    hoverTarget.addEventListener("focusout", (event) => {
      if (!hoverTarget.contains(event.relatedTarget)) reset();
    });
  }

  function init(root) {
    const scope = root && root.nodeType ? root : document;
    scope.querySelectorAll("[data-fps-product-media]").forEach(bindMedia);
  }

  function run(root) {
    const scope = root && root.nodeType ? root : document;

    if (typeof window.initSpecialFrames === "function") {
      window.initSpecialFrames(scope);
    }

    init(scope);
  }

  function scheduleInit() {
    const onReady = () => run(document);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", onReady);
    } else {
      onReady();
    }

    window.addEventListener("load", () => run(document));
  }

  window.initFpsProductMedia = init;
  scheduleInit();

  document.addEventListener("shopify:section:load", (event) => {
    run(event.target || document);
  });
})();
